import {keyframes} from "@emotion/react";
import {
    Box,
    BoxProps,
    Button,
    Input,
    InputGroup,
    InputLeftElement,
    useDisclosure,
    useRadio,
    useRadioGroup,
    Popover,
    PopoverHeader,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    Stack,
} from '@chakra-ui/react';
import {ReactNode} from 'react';
import {useState, useMemo, useEffect} from "react";
import {scrollbar} from "../styling"
import {Option} from "../../models";

interface CustomRadioProps {
    value: string;
    isChecked: boolean;
    children: ReactNode;
}

function CustomRadio(props: CustomRadioProps) {
    const {getInputProps, getRadioProps} = useRadio(props);
    const input = getInputProps();
    const checkbox = getRadioProps();
    let {isChecked} = props;

    return <Box as="label">
        <input {...input} />
        <Box
            {...checkbox}
            cursor="pointer"
            borderWidth="1px"
            borderRadius="md"
            boxShadow="md"
            bg={isChecked ? 'primary.500' : 'gray.100'}
            color={isChecked ? 'white' : 'black'}
            px={4}
            py={2}
            _hover={{bg: isChecked ? 'teal.600' : 'gray.200'}}
            transition="0.2s"
            display="flex"
            gap={".25rem"}
        >
            {
                isChecked ?
                    (<svg width="24" height="24" aria-hidden="true">
                        <use xlinkHref="#check" />
                    </svg>)
                    : <Box width={"24px"} height={"24px"}/>
            }
            <Box flex={"2 1 30%"} textTransform="none">{props.children}</Box>
        </Box>
    </Box>;
}


interface Props {
    defaultValue?: string;
    currentValue?: string;
    name?: string;
    header?: string;
    options: Option[];
    onSelect: (id: string) => void;
    prefix?: string;
}
type SelectProps = Props & BoxProps;

export function Shimmer(props: BoxProps) {
    const blink = keyframes`
0% { opacity: 0.6; }
50% { opacity: 1; }
100% { opacity: 0.6; }
`;

    return (
        <Box
            {...props}
            height={props.height ?? props.h ?? "20px"}
            width={props.width ?? props.w ?? "100%"}
            borderRadius={props.borderRadius ?? "md"}
            bg={props.bg ?? "gray.200"}
            animation={`${blink} 1.5s ease-in-out infinite`}
        />
    );
}

function highlightMatch(text: string, term: string) {
    let i: number;
    if (!term) {
        return text;
    }
    i = text.toLowerCase().indexOf(term.toLowerCase());
    if (i === -1) {
        return text;
    }
    return (
        <>
            {text.slice(0, i)}
            <Box as="span" fontWeight="bold" color="primary.500">
                {text.slice(i, i + term.length)}
            </Box>
            {text.slice(i + term.length)}
        </>
    );
}

export function CustomSelect(props: SelectProps) {
    const {isOpen, onClose, onOpen} = useDisclosure();
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(function () {
        const handler = setTimeout(() => setDebouncedSearch(search), 200);
        return () => clearTimeout(handler);
    }, [search]);

    const filteredOptions = useMemo(function () {
        const term = debouncedSearch.toLowerCase();
        return props.options.filter((opt) =>
            opt.name.toLowerCase().includes(term)
        );
    }, [debouncedSearch, props.options]);

    const {getRootProps, getRadioProps} = useRadioGroup({
        defaultValue: props.defaultValue,
        name: props.name ?? "custom-select",
        onChange: function(val) {
            props.onSelect(val);
            onClose();
            setSearch("");
        },
    });

    return <Popover isOpen={isOpen} onClose={onClose} onOpen={onOpen}>
        <PopoverTrigger>
            <Button
                flex={props.flex}
                gap=".25rem"
                justifyContent="space-between"
                padding={props.p ?? ".25rem .125rem .25rem .25rem"}
                height="45px"
                _focus={{outlineColor: "primary.500", outlineWidth: "1px"}}
                border="none"
                mx="4px"
                borderRadius="5px"
            >
                <Box as="span" color="primary.500" overflowX="hidden" textOverflow="ellipsis">
                    {props.currentValue}
                </Box>
                <svg width="24" height="24" aria-hidden="true">
                    <use xlinkHref="#chevron_selector" />
                </svg>
            </Button>
        </PopoverTrigger>
        {isOpen && <PopoverContent minW="300px" bg="white">
            {props.header && (
                <PopoverHeader
                    color="gray.600"
                    textTransform="uppercase"
                    fontWeight="bold"
                    paddingX="1rem"
                >
                    {props.header}
                </PopoverHeader>
            )}
            <PopoverBody>
                <InputGroup mb={2}>
                    <InputLeftElement pointerEvents="none" w="1.5rem" h="full" left=".25rem" my={"auto"}>
                        <svg width="24" height="24" aria-hidden="true">
                            <use xlinkHref="#search" />
                        </svg>
                    </InputLeftElement>
                    <Input
                        placeholder="Search..."
                        size="sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        borderRadius="md"
                        bg="gray.50"
                        focusBorderColor="primary.500"
                    />
                </InputGroup>
                <Stack {...getRootProps()} maxH={"200px"} overflowY="auto" sx={scrollbar.styled}>
                    {filteredOptions.length === 0 ? <Box px={4} py={2} color="gray.500" fontStyle="italic">
                        No matches found
                    </Box> : filteredOptions.map(function (option) {
                            const radio = Object.assign(
                                getRadioProps({value: option.id}),
                                {isChecked: props.currentValue === option.id, value: option.id}
                            );
                            return <CustomRadio key={option.id} {...radio}>
                                {highlightMatch(option.name, debouncedSearch)}
                            </CustomRadio>;
                        })
                    }
                </Stack>
            </PopoverBody>
        </PopoverContent>}
    </Popover>;
}
